// src/App.js
import React, { useEffect, useRef, useContext  } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate   } from 'react-router-dom';
import M from 'materialize-css'; // Import Materialize JavaScript
import './App.css';
import Home from './pages/Home';
import Introduction from './pages/Introduction';
import Blog from './pages/Blog';
import About from './pages/About';
import Tutorial from './pages/tutorial/Tutorial';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Login from './pages/auth/Login';
import Logout  from './pages/auth/Logout';
import Signup from './pages/auth/Signup';
import Verification from './pages/auth/Verification';
import ResetPassword from './pages/auth/ResetPassword';
import Callback from './pages/auth/Callback';
import Settings from './pages/user/Settings';
import Profile from './pages/user/Profile';
import History from './pages/user/History';
import Password from './pages/user/Password';
import SubjectPreferences from './pages/user/SubjectPreferences';
import HelpCenter from './pages/user/HelpCenter';
import InviteFriends from './pages/user/InviteFriends';
import Notifications from './pages/user/Notifications';
import SubscriptionPlan from './pages/user/SubscriptionPlan';
import Membership from './pages/membership/Membership'; 
import PurchasePlan from './pages/membership/PurchasePlan';
import StudentGrades from './pages/quiz/StudentGrades/StudentGrades';
import SGTailorQuestion from './pages/quiz/StudentGrades/TailorQuestions';
import TailorQuestion from './pages/quiz/Questions/TailorQuestions';
import Questions from './pages/quiz/Questions/Questions';
import Language from './pages/quiz/Language/Language';
import ChineseWriting from './pages/quiz/Language/ChineseWriting';
import ChineseReading from './pages/quiz/Language/Reading';
import GeneralChinese from './pages/quiz/Questions/Questions';
import ChineseClassical from './pages/quiz/Questions/Questions';
import EnglishWriting from './pages/quiz/Language/EnglishWriting';
import EnglishReading from './pages/quiz/Language/Reading';
import GeneralEnglish from './pages/quiz/Questions/Questions';
import Admin from './pages/admin/Administration';
import { AuthProvider, AuthContext } from './js/context/AuthContext';
import Layout from './js/components/Layout/Layout';
import { setupInterceptors } from './js/services/axiosInstance'; // Adjust the import as necessary
import ProtectedRoute from './js/components/ProtectedRoute'; // Import ProtectedRoute
import ServiceWorkerRegistration from './ServiceWorkerRegistration'; // Import the service worker registration component
import './i18n'; // Import your i18n configuration
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n'; // Import your i18n instance
import { fetchUnreadNotificationsCount } from './js/services/api';
import MaintenancePage from './pages/MaintenancePage';
/*
Placement of useContext:
In App Component:
If you place useContext(AuthContext) directly in the App component before the AuthProvider is rendered, it tries to access the context before it has been provided.
This results in undefined, causing the error when you attempt to destructure properties like isAuthenticated.
In MainRoutes Component:
By moving the route logic to a separate MainRoutes component that is a child of AuthProvider, you ensure that useContext(AuthContext) is called after the context has been provided.
This avoids the error since MainRoutes is correctly wrapped in AuthProvider.
 */

const App = () => {
// Check the environment variable
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === '1';

  // If active, ONLY render the maintenance page
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }
  
/*
Position of useContext:
If calling useContext(AuthContext) at the beginning of your App component, 
the AuthProvider that provides this context is placed after the Router component. 
This means that when you try to access the context, it’s not available yet because the provider hasn't been rendered.
Therefore, the AuthProvider that provides this context is placed befores3 the Router component. 
 */
  return (
    <I18nextProvider i18n={i18n}>
<AuthProvider>
   <ServiceWorkerRegistration /> {/*   Register the service worker here */}
    <Router> {/* Wrap with Router */}
        <Layout> {/* Wrap Routes with Layout */}
     
                        <MainRoutes /> {/* Separate component for routes */}
                   
        </Layout>
    </Router>
    </AuthProvider>
    </I18nextProvider>
  );
};


// Create a separate component for routes
const MainRoutes = () => {
  const { isAuthenticated, user, handleTokenRefresh } = useContext(AuthContext); // Access context
  const isInterceptorsSet = useRef(false); // Ref to track if interceptors have been set
  const navigate = useNavigate();

   useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const target = params.get('target');

        if (target) {
            navigate(`/${target}`); // Navigate to the target page
        }
    }, [navigate]);

  useEffect(() => {
    // Initialize Materialize components
    M.AutoInit();
  }, []);

  useEffect(() => {
      
    if (!isInterceptorsSet.current) {
    setupInterceptors(handleTokenRefresh); // Set up interceptors with the token refresh function
    isInterceptorsSet.current = true; // Mark as set
    }

}, [handleTokenRefresh, user, isAuthenticated]);

// Function to update the app badge
// This function is called when the user logs in or when the unread count changes
const updateBadge = async (count) => {
    if ('setAppBadge' in navigator) {
        try {
            await navigator.setAppBadge(count);
        } catch (error) {
            console.error('Failed to set app badge:', error);
        }
    } else {
        console.warn('setAppBadge is not supported in this browser.');
    }
};

useEffect(() => {
    const fetchInboxCount = async () => {
        if (!user) return; // Early return if user is not defined
        try {
            const count = await fetchUnreadNotificationsCount();
            updateBadge(count);
        } catch (error) {
            console.error('Error fetching inbox count:', error);
        }
    };

    fetchInboxCount(); // Initial fetch
    const interval = setInterval(fetchInboxCount, 24 * 60 * 60 * 1000); // Fetch every  24 hours
    return () => clearInterval(interval); // Cleanup on unmount
}, [user]); // Dependency array to trigger effect when user or unreadCount changes


  // Safely parse user data
  const parsedUser = user ? (typeof user === 'string' ? JSON.parse(user) : user) : null;

  return (
      <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/introduction" element={<Introduction />} />
          <Route path="/about" element={<Introduction />} />
          <Route path="/tutorial/:tutorial_topic" element={<Tutorial />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<Verification />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/callback" element={<Callback />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/edit-account" 
              element={
                  <ProtectedRoute 
                      element={<Profile />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />          
          <Route path="/setting" 
              element={
                  <ProtectedRoute 
                      element={<Settings />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />          
          <Route path="/history" 
          element={
              <ProtectedRoute 
                  element={<History />} 
                  isAuthenticated={isAuthenticated} 
              />
          } 
      /> 
      <Route path="/password" 
      element={
          <ProtectedRoute 
              element={<Password />} 
              isAuthenticated={isAuthenticated} 
          />
      } 
  />
        <Route path="/subject-preferences" 
        element={
            <ProtectedRoute 
                element={<SubjectPreferences />} 
                isAuthenticated={isAuthenticated} 
            />
        } 
        />
        <Route path="/invite-friends" 
        element={
            <ProtectedRoute 
                element={<InviteFriends />} 
                isAuthenticated={isAuthenticated} 
            />
        } 
        />  
          <Route path="/membership" 
              element={
                  <ProtectedRoute 
                      element={<Membership />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />         
          <Route path="/buy" 
              element={
                  <ProtectedRoute 
                      element={<PurchasePlan />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />            
          <Route path="/complete-order" 
              element={
                  <ProtectedRoute 
                      element={<PurchasePlan />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />         
          <Route path="/membership-subscription" 
              element={
                  <ProtectedRoute 
                      element={<SubscriptionPlan />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />          
        <Route path="/quiz" 
            element={
                  <ProtectedRoute 
                      element={<StudentGrades />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />
          <Route path="/quiz-tailor-question/:grade/:subject" 
              element={
                    <ProtectedRoute 
                        element={<SGTailorQuestion />} 
                        isAuthenticated={isAuthenticated} 
                    />
                } 
            />
          <Route path="/tailor-question/:grade/:subject" 
                element={
                    <ProtectedRoute 
                        element={<TailorQuestion />} 
                        isAuthenticated={isAuthenticated} 
                    />
                } 
            />
          <Route path="/question/:grade/:subject" 
                element={
                    <ProtectedRoute 
                        element={<Questions />} 
                        isAuthenticated={isAuthenticated} 
                    />
                } 
            />
          <Route path="/chinese/:grade/:subject" 
              element={
                  <ProtectedRoute 
                      element={<Language />} 
                      isAuthenticated={isAuthenticated} 
                  />
              } 
          />
          <Route path="/chinese-writing/:grade/:subject" 
            element={
                <ProtectedRoute 
                    element={<ChineseWriting />} 
                    isAuthenticated={isAuthenticated} 
                />
            } 
            />
            <Route path="/chinese-reading/:grade/:subject" 
            element={
                <ProtectedRoute 
                    element={<ChineseReading />} 
                    isAuthenticated={isAuthenticated} 
                />
            } 
         />
            <Route path="/general-chinese/:grade/:subject" 
                element={
                    <ProtectedRoute 
                        element={<GeneralChinese />} 
                        isAuthenticated={isAuthenticated} 
                    />
                } 
            />
            <Route path="/chinese-classical/:grade/:subject" 
                element={
                    <ProtectedRoute 
                        element={<ChineseClassical />} 
                        isAuthenticated={isAuthenticated} 
                    />
                } 
            />
            <Route path="/english/:grade/:subject" 
            element={
                <ProtectedRoute 
                    element={<Language />} 
                    isAuthenticated={isAuthenticated} 
                />
            } 
        />
            <Route path="/english-writing/:grade/:subject" 
                element={
                    <ProtectedRoute 
                        element={<EnglishWriting />} 
                        isAuthenticated={isAuthenticated} 
                    />
                } 
            />
            <Route path="/english-reading/:grade/:subject" 
            element={
                <ProtectedRoute 
                    element={<EnglishReading />} 
                    isAuthenticated={isAuthenticated} 
                />
            } 
            />
            <Route path="/general-english/:grade/:subject" 
            element={
                <ProtectedRoute 
                    element={<GeneralEnglish />} 
                    isAuthenticated={isAuthenticated} 
                />
            } 
            />
            <Route path="/inbox" 
            element={
                <ProtectedRoute 
                    element={<Notifications />} 
                    isAuthenticated={isAuthenticated} 
                />
            } 
            />
        {/* Admin Route */}
             {parsedUser && parsedUser.role === 'admin' && (
                <Route path="/admin">
                    <Route index element={<Admin />} />
                    <Route path="*" element={<Admin />} />
                </Route>
            )}
          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
};
export default App;